/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The card, header, and button styling every settings block shares.
 *
 * Extracted when the sale settings grew a second and third block: three
 * copies of the same tokens is three chances for one of them to drift.
 */

import type React from 'react';

/**
 * One settings block, as the subscription area draws its cards: a section
 * with the header inside it and the content flush below. Kept as a class
 * string, identical to `SubscriptionHistoryCard`'s, so the two areas cannot
 * drift apart on a token change.
 */
export const SETTINGS_CARD =
  'border border-(--border-subtle) bg-(--surface-secondary) p-(--space-5) rounded-(--radius-lg) ' +
  'transition-[border-color,background-color] duration-[var(--transition-standard)]';

export const containerStyle: React.CSSProperties = {
  background: 'var(--surface-primary)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-lg)',
  overflow: 'hidden',
};

/**
 * Header strip for a settings block: title, a count caption and the block's
 * primary action. Sits *above* the content rather than inside a card of its
 * own — `ListingTable` already draws a card, and wrapping it in a second one
 * nested a card inside a card. Mirrors `TeamSection`'s header.
 */
export const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
  gap: 'var(--space-4)', flexWrap: 'wrap',
  marginBottom: 'var(--space-4)',
};

/** The title + caption column of a header strip; yields to the action. */
export const headerTextStyle: React.CSSProperties = {
  flex: '1 1 20rem', minWidth: 0,
};

export const titleStyle: React.CSSProperties = {
  margin: 0, fontSize: 'var(--text-app-section)', fontWeight: 700,
  color: 'var(--text-primary)', lineHeight: 1.2,
};

/** The count line under a block title, as the team header does it. */
export const subtitleStyle: React.CSSProperties = {
  margin: 'var(--space-0-5) 0 0',
  fontSize: 'var(--text-small)',
  color: 'var(--text-tertiary)',
  lineHeight: 1.5,
  // The explanation lives here now instead of floating above the title, so
  // it needs a measure — a full-width line of tertiary text reads as debris.
  maxWidth: '68ch',
};

/**
 * The caveat line under a table — attached to the table it qualifies rather
 * than floating as another paragraph in the column.
 */
export const tableCaptionStyle: React.CSSProperties = {
  margin: 'var(--space-3) 0 0',
  padding: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--text-tertiary)',
  lineHeight: 1.5,
};

/** Shown in place of an empty table, rather than a header with no rows. */
export const tableEmptyStyle: React.CSSProperties = {
  padding: 'var(--space-8)', textAlign: 'center',
  fontSize: 'var(--text-body-sm)', color: 'var(--text-tertiary)',
};

export const addBtnStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-body-sm)',
  fontWeight: 600, background: 'var(--button-primary-bg)', color: 'var(--button-primary-text)',
  border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer',
  fontFamily: 'var(--font-family)',
};

export const editBtnStyle: React.CSSProperties = {
  fontSize: 'var(--text-body-sm)', fontWeight: 500, color: 'var(--text-secondary)',
  background: 'transparent', border: 'none', padding: 'var(--space-1) var(--space-1)',
  borderRadius: 'var(--radius-sm)', cursor: 'pointer',
  fontFamily: 'var(--font-family)', textDecoration: 'underline',
  textUnderlineOffset: '3px', textDecorationColor: 'var(--border-medium)',
};

export const labelStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  fontFamily: 'var(--font-family)',
};

export const cancelBtnStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-4)',
  fontSize: 'var(--text-body-sm)',
  fontWeight: 600,
  background: 'transparent',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
};

/**
 * Builds inline styles for the modal's primary action button.
 * @param disabled - Whether the button is disabled.
 * @returns The CSS properties for the primary button.
 */
export const primaryBtnStyle = (disabled: boolean): React.CSSProperties => ({
  padding: 'var(--space-2) var(--space-4)',
  fontSize: 'var(--text-body-sm)',
  fontWeight: 600,
  background: disabled ? 'var(--surface-secondary)' : 'var(--button-primary-bg)',
  color: disabled ? 'var(--text-tertiary)' : 'var(--button-primary-text)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: 'var(--font-family)',
  transition: 'var(--transition-fast)',
});


/* The block pattern now lives in the design system, since the subscription
   area draws the same blocks. Re-exported here so the sections that already
   import from this module keep one import. */
export {
  blockCardStyle, blockHeaderStyle, blockHeaderTextStyle, blockTitleStyle,
  blockCaptionStyle, blockFieldsGridStyle, fieldLabelStyle, fieldValueStyle,
  blockNoteStyle,
} from '@/shared/ui/components/composed/settings-block/settings-block.styles';
